import React, { Fragment, useCallback, useRef, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Row, Col, Button, Collapse, Form } from 'reactstrap';
import { Button as ScrollButton } from 'react-scroll';
import { omit } from 'lodash-es';
import {
  APPRAISER_STEP_SELECT_TRACKING,
  APPRAISER_STEP_BUTTON_TRACKING,
  REPORT,
  LICENSE_PLATE_AND_VIN,
  INSTANT_OFFER,
  LOCATION_AND_STYLE,
  CONDITION_AND_MILEAGE,
  PARTNER_OFFER_CONDITIONS,
  COLORS_AND_OPTIONS,
  EMAIL,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { PAYMENT_CALCULATOR_NAME } from 'site-modules/shared/constants/calculator/calculator';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { TradeInAppraiserItemContinueBtn } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-item-continue-btn';
import { AnimatedCollapse } from 'site-modules/shared/utils/collapse-utils';

const SCROLL_OFFSET = -48;
export const EMO_4318_COLLAPSE_DURATION = 360;
export const EMO_4318_LONG_COLLAPSE_DURATION = 600;
export const EMO_4318_SHORT_COLLAPSE_DURATION = 250;
const EMO_4318_CHECKMARK_ANIMATION_DURATION = 500;
const EMO_4318_LONG_COLLAPSE_STEPS = [COLORS_AND_OPTIONS, PARTNER_OFFER_CONDITIONS];
const STEPS_WITHOUT_NEXT_STEP_BTN = [REPORT, LICENSE_PLATE_AND_VIN, LOCATION_AND_STYLE, EMAIL];
const EMO_4318_VIEWPORT_RESERVED_HEIGHT = 'calc(100vh + 52px)';

export const TradeInAppraiserItem = ({
  title,
  id,
  children,
  nextId = '',
  nextStepTitle = '',
  stepCounter,
  isEmo4318Chal1 = false,
  isHiddenForRenewal = false,
  isStepClosing = false,
  isStepOpening = false,
  isStepOpenDelayed = false,
  onStepCollapseComplete = false,
  onStepAnimationComplete = null,
  onDone = null,
  onContinue = null,
  onSave = null,
  isDone = false,
  isNavigatingBetweenDoneSteps = false,
  isNextStepDone = false,
  isActive = false,
  isFinal = false,
  hasTotalPayment = false,
  isExtendedReport = false,
  trackingParentId = null,
  appliedTradeIn = 0,
  predefinedVin = '',
  isQueryPredefinedVin = false,
  styleIds = '',
  isEscapeHatch = false,
  isLoadingColorsAndOptions = false,
  isPaymentLimitsLoading = false,
  isMobile = false,
  isOfferConditionsStepLoaded = false,
  isLicensePlateAndVinStepLoaded = false,
  isInstantOfferStepLoaded = false,
  vin = '',
  scrollToOnApply = PAYMENT_CALCULATOR_NAME,
}) => {
  const stepHeaderRef = useRef();
  const stickyStepHeaderRef = useRef();
  const isCollapseAnimationCompleteRef = useRef(false);
  const isCheckmarkAnimationCompleteRef = useRef(false);
  const [isStepContentVisible, setIsStepContentVisible] = useState(!isEmo4318Chal1);
  const [isStepCollapsed, setIsStepCollapsed] = useState(false);
  const [isStickyStepHeaderStuck, setIsStickyStepHeaderStuck] = useState(false);
  const [isAnimatedCollapseInitialized, setIsAnimatedCollapseInitialized] = useState(!isEmo4318Chal1);

  const handleSubmit = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  useEffect(() => {
    if (isActive && !isHiddenForRenewal && !isStepOpenDelayed) {
      setTimeout(() => {
        stepHeaderRef.current?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isActive, isHiddenForRenewal, isStepOpenDelayed]);

  useEffect(() => {
    if (!isStepClosing) {
      isCollapseAnimationCompleteRef.current = false;
      isCheckmarkAnimationCompleteRef.current = false;
    }
  }, [isStepClosing]);

  useEffect(() => {
    if (!(isMobile && isEmo4318Chal1 && isActive && !isStepClosing) || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyStepHeaderStuck(entry.intersectionRatio < 1);
      },
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    );

    observer.observe(stickyStepHeaderRef.current);

    return () => observer.disconnect();
  }, [isActive, isEmo4318Chal1, isMobile, isStepClosing]);

  const isButtonDisabled = isFinal ? !hasTotalPayment || isPaymentLimitsLoading : !isDone;
  const isStepDone = (isDone && (!isActive || isStepClosing)) || (isEmo4318Chal1 && isStepClosing);
  const isStepDisabled = isStepClosing || isActive || !isDone;
  const isFinalActive = isFinal && isActive;
  const buttonColor = classNames({ secondary: isButtonDisabled, success: !isButtonDisabled });
  const finalButtonLinkClassNames = {
    'btn-secondary disabled': isButtonDisabled,
    'btn-success': !isButtonDisabled,
  };
  const addToPaymentEstimateBtnProps = {
    'data-tracking-id': APPRAISER_STEP_BUTTON_TRACKING[id],
    className: classNames('with-vertical-top-line form-button pos-r d-block w-100 btn', finalButtonLinkClassNames),
    type: 'submit',
    value: `Apply ${formatPriceStringV2(appliedTradeIn)} to my deal`,
    onClick: onSave,
    color: buttonColor,
    disabled: isButtonDisabled,
  };

  const isOfferConditionsStep = id === PARTNER_OFFER_CONDITIONS;

  const isNextStepLoaded = useMemo(() => {
    if (nextId === PARTNER_OFFER_CONDITIONS) {
      return isOfferConditionsStepLoaded;
    } else if (nextId === COLORS_AND_OPTIONS) {
      return !isLoadingColorsAndOptions;
    } else if ((nextId === LICENSE_PLATE_AND_VIN && vin) || nextId === INSTANT_OFFER) {
      return isInstantOfferStepLoaded;
    } else if (nextId === LICENSE_PLATE_AND_VIN) {
      return isLicensePlateAndVinStepLoaded;
    }
    return true;
  }, [
    nextId,
    vin,
    isOfferConditionsStepLoaded,
    isLoadingColorsAndOptions,
    isInstantOfferStepLoaded,
    isLicensePlateAndVinStepLoaded,
  ]);

  const isVinWithStyleIds = isQueryPredefinedVin && predefinedVin && styleIds?.split(',')?.length >= 1;

  const isLocationAndStyleNextStepBtnDisplayed =
    id === LOCATION_AND_STYLE && (!isExtendedReport || (isVinWithStyleIds && !isMobile));

  const isNextStepBtnDisplayed =
    (!isFinal && !STEPS_WITHOUT_NEXT_STEP_BTN.includes(id)) || isLocationAndStyleNextStepBtnDisplayed;

  const nextStepBtn = useMemo(
    () => (
      <Row className={classNames('mt-2', { 'mt-md-2_5': id === LOCATION_AND_STYLE && isExtendedReport })}>
        <Col xs={12} md={4} className="px-0">
          <TradeInAppraiserItemContinueBtn
            id={id}
            stepCounter={stepCounter}
            nextStepTitle={nextStepTitle}
            onContinue={onContinue}
            nextId={nextId}
            isNextStepLoaded={isNextStepLoaded}
          />
        </Col>
      </Row>
    ),
    [id, isExtendedReport, isNextStepLoaded, nextId, nextStepTitle, onContinue, stepCounter]
  );

  const useFormTag = !(id === LICENSE_PLATE_AND_VIN || id === INSTANT_OFFER);

  let xlColSize = [LOCATION_AND_STYLE, CONDITION_AND_MILEAGE].includes(id) ? 10 : 12;

  if (id === INSTANT_OFFER) {
    xlColSize = 11;
  }

  const stepContentClassNames = useMemo(
    () => ({
      'pb-3': id !== INSTANT_OFFER,
      'mt-1_5': !isExtendedReport || (isExtendedReport && (id !== INSTANT_OFFER || !isMobile)),
      'mt-0': isExtendedReport && id === INSTANT_OFFER && isMobile,
    }),
    [id, isExtendedReport, isMobile]
  );

  const stepContent = (
    <div className={classNames('pos-r px-md-3', isOfferConditionsStep ? 'px-1' : 'px-1_5', stepContentClassNames)}>
      {isExtendedReport ? (
        <Fragment>
          <Row>
            <Col
              xs={12}
              xl={xlColSize}
              className={classNames({
                'mx-xl-auto': id === INSTANT_OFFER,
                'px-xl-0': id === INSTANT_OFFER && isEscapeHatch,
              })}
            >
              {children}
            </Col>
          </Row>
          {isNextStepBtnDisplayed && (
            <Row>
              <Col xs={12} xl={10}>
                {nextStepBtn}
              </Col>
            </Row>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <Row>
            <Col xs={12}>{children}</Col>
          </Row>
          {isNextStepBtnDisplayed && nextStepBtn}
        </Fragment>
      )}
    </div>
  );

  const stepContentWithAnimation = isEmo4318Chal1 ? (
    <div
      className={classNames({
        'step-content-fade': isEmo4318Chal1,
        'step-content-fade-visible': isEmo4318Chal1 && isStepContentVisible && !isStepClosing,
        'step-content-fade-closing': isEmo4318Chal1 && isStepClosing,
      })}
    >
      {stepContent}
    </div>
  ) : (
    stepContent
  );

  const handleAnimatedCollapseInit = useCallback(() => {
    setIsAnimatedCollapseInitialized(true);
  }, []);

  const completeClosingStepIfReady = useCallback(() => {
    if (isCollapseAnimationCompleteRef.current && isCheckmarkAnimationCompleteRef.current && isStepClosing) {
      isCollapseAnimationCompleteRef.current = false;
      isCheckmarkAnimationCompleteRef.current = false;
      onStepCollapseComplete?.(id);
    }
  }, [id, isStepClosing, onStepCollapseComplete]);

  useEffect(() => {
    if (!isStepClosing || !isStepCollapsed) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const checkmarkAnimationTimerId = setTimeout(
      () => {
        isCheckmarkAnimationCompleteRef.current = true;
        completeClosingStepIfReady();
      },
      prefersReducedMotion ? 0 : EMO_4318_CHECKMARK_ANIMATION_DURATION
    );

    return () => clearTimeout(checkmarkAnimationTimerId);
  }, [completeClosingStepIfReady, isStepCollapsed, isStepClosing]);

  const handleAnimatedCollapseChange = useCallback(
    ({ state }) => {
      const isCollapsed = state === 'collapsed';

      setIsStepContentVisible(state === 'expanded');
      setIsStepCollapsed(isCollapsed);
      if (isCollapsed && isStepClosing) {
        isCollapseAnimationCompleteRef.current = true;
        completeClosingStepIfReady();
      }
    },
    [completeClosingStepIfReady, isStepClosing]
  );

  useEffect(() => {
    if (!isEmo4318Chal1 || !isActive || isStepClosing || isStepOpenDelayed || !isStepContentVisible) {
      return undefined;
    }
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // The content fades in for 300ms after the height animation expands.
    // Keep delay the same as .step-content-fade's animation duration in trade-in-appraiser.scss
    const fadeTimerId = setTimeout(() => onStepAnimationComplete?.(id), prefersReducedMotion ? 0 : 300);
    return () => clearTimeout(fadeTimerId);
  }, [id, isEmo4318Chal1, isActive, isStepClosing, isStepOpenDelayed, isStepContentVisible, onStepAnimationComplete]);

  const baseCollapseDuration = EMO_4318_LONG_COLLAPSE_STEPS.includes(id)
    ? EMO_4318_LONG_COLLAPSE_DURATION
    : EMO_4318_COLLAPSE_DURATION;
  const collapseDuration = isNavigatingBetweenDoneSteps ? EMO_4318_SHORT_COLLAPSE_DURATION : baseCollapseDuration;

  const stepCollapse = isEmo4318Chal1 ? (
    <AnimatedCollapse
      isOpen={isAnimatedCollapseInitialized && isActive && !isHiddenForRenewal && !isStepClosing && !isStepOpenDelayed}
      transition={`height ${collapseDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`}
      onInit={handleAnimatedCollapseInit}
      onChange={handleAnimatedCollapseChange}
      data-tracking-parent={trackingParentId}
      data-testid={`${id}-item-collapse`}
    >
      {stepContentWithAnimation}
    </AnimatedCollapse>
  ) : (
    <Collapse
      isOpen={isActive && !isHiddenForRenewal}
      data-tracking-parent={trackingParentId}
      data-testid={`${id}-item-collapse`}
    >
      {stepContent}
    </Collapse>
  );

  const StepTitle = isExtendedReport ? 'h2' : 'h3';

  const shouldHide = !isActive && !isDone;
  const isEmailStep = EMAIL === id;
  const hasReservedViewportSpace =
    isMobile &&
    isEmo4318Chal1 &&
    (isStepOpening || (isActive && (!isNextStepDone || isStepOpenDelayed || !isStepContentVisible)));

  return (
    <Form
      tag={!useFormTag ? 'div' : 'form'}
      /* See EVAL-2798 for edge case handling */
      action={useFormTag ? 'javascript:void(0);' : undefined} // eslint-disable-line no-script-url
      onSubmit={handleSubmit}
      className={classNames('pos-r trade-in-appraiser-item', id, {
        'pb-1_5': !isFinal && !isExtendedReport,
        'mb-1_5': isFinal,
        done: isStepDone,
        active: isActive && !isStepClosing,
        'step-closing': isStepClosing,
        'emo-4318-chal1': isEmo4318Chal1,
        [isEmailStep ? 'd-none' : 'invisible']: shouldHide,
        'thicker-form-items': [LOCATION_AND_STYLE, EMAIL].includes(id),
        'mb-1_75': isEmailStep && isActive,
        'extended-report mt-0': isExtendedReport,
        'pt-0': isEmo4318Chal1,
        'bg-transparent': hasReservedViewportSpace,
      })}
      autoComplete={useFormTag ? 'off' : undefined}
      data-testid={`${id}-item`}
      noValidate
      style={hasReservedViewportSpace ? { minHeight: EMO_4318_VIEWPORT_RESERVED_HEIGHT } : undefined}
    >
      <div className={classNames('form-content pos-r bg-white', { 'rounded mt-0_75': !isExtendedReport })}>
        <StepTitle
          ref={stickyStepHeaderRef}
          className={classNames('m-0', {
            'sticky-step-header': isMobile && isEmo4318Chal1 && isActive && !isStepClosing,
            'sticky-step-header-stuck':
              isStickyStepHeaderStuck && isMobile && isEmo4318Chal1 && isActive && !isStepClosing,
          })}
        >
          <Button
            data-index={id}
            data-tracking-id={APPRAISER_STEP_SELECT_TRACKING}
            data-testid={`step-header-${id}`}
            disabled={isStepDisabled}
            onClick={onContinue}
            className={classNames('step-header d-flex align-items-center text-decoration-none border-0', {
              'p-0 ms-1 pe-1_25 size-16 bg-transparent mt-0 py-0_75 text-blue-40': isExtendedReport && !isEmo4318Chal1,
              'size-16 w-100 text-start mt-0 py-0_75 px-1_25 text-cool-gray-10 bg-white':
                isExtendedReport && isEmo4318Chal1,
              'p-0 ms-1 pe-1_25 size-16 bg-white ms-md-1_5': !isExtendedReport,
            })}
            innerRef={stepHeaderRef}
            {...(isActive && { tabIndex: -1 })}
          >
            {isExtendedReport && isStepDone && (
              <div className="step-counter-extended-report d-flex align-items-center justify-content-center me-1">
                <div className="check-container">
                  {isEmo4318Chal1 ? (
                    <svg
                      className={classNames('animated-checkmark', {
                        'animated-checkmark--animating': isStepClosing && isStepCollapsed,
                      })}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 52 52"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle className="animated-checkmark__circle" cx={26} cy={26} r={25} />
                      <path className="animated-checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                    </svg>
                  ) : (
                    <div className="check" />
                  )}
                </div>
              </div>
            )}
            {((isExtendedReport && !isStepDone) || !isExtendedReport) && (
              <span
                data-index={id}
                className={classNames(
                  'step-counter rounded-circle d-inline-flex align-items-center justify-content-center me-1 size-14 fw-bold',
                  {
                    'bg-blue-50 text-white': isActive,
                    'icon-done text-success': isStepDone,
                  }
                )}
              >
                {isActive && <span className="visually-hidden">Current Step</span>}
                {!isStepDone && stepCounter}
              </span>
            )}
            <span
              data-index={id}
              className={classNames('step-label', { 'text-shadow-contrast': isExtendedReport && isStepDone })}
            >
              {title()}
            </span>
          </Button>
        </StepTitle>
        {isExtendedReport && isActive && !isStepClosing && isStepContentVisible && <hr className="m-0" />}
        {isExtendedReport && !isEmo4318Chal1 ? (
          <div
            data-tracking-parent={trackingParentId}
            data-testid={`step-content-${id}`}
            className={classNames('step-content', { 'd-none': !isActive })}
          >
            {stepContent}
          </div>
        ) : (
          stepCollapse
        )}
      </div>
      {!isExtendedReport && isFinalActive && (
        <Row className="mt-1_5">
          <Col xs={12} md={7}>
            {!!scrollToOnApply && (
              <ScrollButton
                to={scrollToOnApply}
                tabIndex={0}
                offset={SCROLL_OFFSET}
                smooth
                {...addToPaymentEstimateBtnProps}
              />
            )}
            {!scrollToOnApply && (
              <Button {...omit(addToPaymentEstimateBtnProps, 'value')} data-testid="estimate-btn">
                {isPaymentLimitsLoading && (
                  <span className="pe-0_5">
                    <Spinner size={14} thickness={1} color="white" />
                  </span>
                )}
                {addToPaymentEstimateBtnProps.value}
              </Button>
            )}
          </Col>
          <Col xs={12} md={5}>
            <Button
              color="link"
              onClick={onDone}
              data-tracking-id="close_report"
              data-tracking-value="close button"
              block
              className="text-decoration-none text-center text-md-start ps-md-1 text-primary-darker"
            >
              Close
            </Button>
          </Col>
        </Row>
      )}
    </Form>
  );
};

TradeInAppraiserItem.propTypes = {
  title: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  nextId: PropTypes.string,
  nextStepTitle: PropTypes.string,
  stepCounter: PropTypes.number.isRequired,
  onDone: PropTypes.func,
  onContinue: PropTypes.func,
  onSave: PropTypes.func,
  isDone: PropTypes.bool,
  isNavigatingBetweenDoneSteps: PropTypes.bool,
  isNextStepDone: PropTypes.bool,
  isActive: PropTypes.bool,
  isFinal: PropTypes.bool,
  hasTotalPayment: PropTypes.bool,
  isExtendedReport: PropTypes.bool,
  trackingParentId: PropTypes.string,
  appliedTradeIn: PropTypes.number,
  predefinedVin: PropTypes.string,
  isQueryPredefinedVin: PropTypes.bool,
  styleIds: PropTypes.string,
  isEscapeHatch: PropTypes.bool,
  isLoadingColorsAndOptions: PropTypes.bool,
  isPaymentLimitsLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  isOfferConditionsStepLoaded: PropTypes.bool,
  isLicensePlateAndVinStepLoaded: PropTypes.bool,
  isInstantOfferStepLoaded: PropTypes.bool,
  vin: PropTypes.string,
  scrollToOnApply: PropTypes.string,
  isEmo4318Chal1: PropTypes.bool,
  isHiddenForRenewal: PropTypes.bool,
  isStepClosing: PropTypes.bool,
  isStepOpening: PropTypes.bool,
  isStepOpenDelayed: PropTypes.bool,
  onStepCollapseComplete: PropTypes.func,
  onStepAnimationComplete: PropTypes.func,
};
